import moment from 'moment';
import { createMutations } from '@/utils/vuexHelper';
import { BIDDING_API, SCRIPTING_API } from '@/api';
import dateStore from '@/utils/dateStore';
import { HERows, isEmpty } from '@/utils/dataUtil';
import REFERENCE_DATA_STORE from '../../referenceDataStore';
import { EMPTY_HOURLY_VALUE, HOURLY_VALUES_CONFIG } from '../static';

const state = {
  startDate: dateStore.getDefaultDateLocal().toISOString(),
  hourlyType: undefined,
  hourlyValuesData: [],
  variableDialogVisibility: false,
  dailyGlobalVariablesData: [],
  hourlyValuesConfig: undefined,
  auditData: [],
  commodity: '',
  market: '',
  entityType: '',
  moduleName: null,
  scriptCategories: [],
  scripts: [],
};

const getters = {
  scriptList: (state) => {
    const scriptCategory = state.scriptCategories
      .find(({ name }) => name === `${ state.market.toLowerCase() }-${ state.moduleName.toLowerCase() }-strategies`);
    const scripts = (scriptCategory) ? state.scripts.filter(({ categoryId }) => categoryId === scriptCategory.id) : [];
    return [...new Set(scripts.map(({ name }) => name))];
  },
};

const actions = {
  async init({ dispatch }) {
    await dispatch('fetchScripts');
    await dispatch('fetchStrategyVariables');
    await dispatch('fetchScriptCategories');
  },
  setPageParams({ commit }, {
    commodity,
    entityType,
    market,
    moduleName,
  }) {
    commit('setCommodity', commodity);
    commit('setEntityType', entityType);
    commit('setMarket', market);
    commit('setModuleName', moduleName);
  },
  async fetchScripts({ commit }) {
    try {
      const { data } = await SCRIPTING_API.get();
      commit('setScripts', data.data.map((props) => props));
    } catch (error) {
      console.error('Error Fetching Strategies', error);
    }
  },
  async createVariable({ state, dispatch }, { variableData, type, strategy }) {
    let resp;
    const reqBody = {
      commodity: state.commodity,
      market: state.market,
      entityType: state.entityType,
      module: state.moduleName,
      marketType: variableData.marketType,
      location: variableData.location,
      carryForward: variableData.carryForward,
      variables: [{
        ...variableData,
        startTime: state.startDate,
        endTime: null,
        type,
        scriptName: strategy,
      }],
    };

    if (strategy) {
      try {
        resp = await BIDDING_API.post(`/variables/${strategy}`, reqBody);
      } catch (error) {
        console.error('Error Posting Strategy Variable', error);
      }
    } else {
      try {
        resp = await BIDDING_API.post('/variables', reqBody);
      } catch (error) {
        console.error('Error Posting Global Variable', error);
      }
    }
    await dispatch('fetchStrategyVariables');
    return resp.data;
  },
  async updateVariable({ state, dispatch }, {
    variableData, type, strategy, marketType, location, carryForward, prevCarryForward, visualOrder, valuesOnly,
  }) {
    let variables;

    const tz = dateStore.getTimeZone();
    const momentStartTime = dateStore.toDateFromLocal(state.startDate, tz);
    const totalDayHours = dateStore.getTotalDatehours(state.startDate, tz);

    if (type === 'DAILY') {
      variableData.value = variableData.value.trim();
      variables = [{
        ...variableData,
        startTime: momentStartTime.toISOString(),
        endTime: null,
        type,
        scriptName: strategy,
      }];
    } else {
      const config = variableData || state.hourlyValuesConfig;
      const { id, name, valueType } = config;
      const formattedValuesData = [];
      marketType = state.hourlyValuesConfig?.marketType;
      strategy = strategy || state.hourlyValuesConfig?.strategies?.toString();

      const indexMap = HOURLY_VALUES_CONFIG(state.startDate).columns.map((x) => x.prop);

      indexMap.forEach((x) => {
        if (x !== 'id' && x !== 'description') {
          formattedValuesData.push({
            he: x.replace('he', ''),
            value: state.hourlyValuesData[0][x]?.trim() ?? '',
          });
        }
      });
      variables = formattedValuesData.map(({ he, value }, index) => ({
        id,
        name,
        type,
        value,
        valueType,
        startTime: momentStartTime.clone().add(index, 'hours').toISOString(),
        endTime: momentStartTime.clone().add(index + 1, 'hours').toISOString(),
        carryForward: (totalDayHours != 24
                        || moment().startOf('day').isAfter(momentStartTime))
                      ? prevCarryForward : carryForward,
        scriptName: strategy,
        marketType,
        location,
        visualOrder,
      }),
      );
    }

    const reqBody = {
      commodity: state.commodity,
      market: state.market,
      entityType: state.entityType,
      module: state.moduleName,
      marketType: marketType || null,
      location,
      variables,
      tz,
    };

    if (strategy) {
      try {
        if (valuesOnly) await BIDDING_API.put(`/variables/${strategy}`, reqBody);
        else await BIDDING_API.post(`/variables/${strategy}`, reqBody);
      } catch (error) {
        console.error('Error Posting Strategy Variable', error);
      }
    } else {
      try {
        if (valuesOnly) await BIDDING_API.put('/variables', reqBody);
        else await BIDDING_API.post('/variables', reqBody);
      } catch (error) {
        console.error('Error Posting Global Variable', error);
      }
    }
    await dispatch('fetchStrategyVariables');
  },
  async deleteVariable({ state, commit }, id) {
    try {
      await BIDDING_API.delete(`/variables/${id}`);
      const hourlyValues = HERows({}, true, state.startDate).map(({ he }) => ({ value: undefined, he }));
      commit('setHourlyValuesData', hourlyValues);
    } catch (error) {
      console.error('Error Deleting Variable', error);
    }
  },
  async fetchStrategyVariables({ state, dispatch }, params = { strategy: undefined }) {
    const {
      commodity, entityType, market, moduleName, startDate,
    } = state;

    const tz = dateStore.getTimeZone();
    const momentStartTime = dateStore.toDateFromLocal(startDate, tz);

    const reqBody = {
      commodity,
      market,
      entityType,
      module: moduleName,
      tradingDate: momentStartTime.toISOString(),
      tz,
    };
    if (params.strategy) {
      try {
        const hourlyData = await BIDDING_API.get(
          `/variables/${ params.strategy }`,
          { params: { ...reqBody, type: 'HOURLY' } },
        );
        const dailyData = await BIDDING_API.get(
          `/variables/${ params.strategy }`,
          { params: { ...reqBody, type: 'DAILY' } },
        );
        const combinedData = { hourly: hourlyData.data, daily: dailyData.data };
        dispatch('mapVariables', { combinedData, strategy: params.strategy });
      } catch (error) {
        console.error('Error Fetching Hourly Strategy Variables', error);
      }
    } else {
      try {
        const hourlyData = await BIDDING_API.get('/variables', { params: { ...reqBody, type: 'HOURLY' } });
        const dailyData = await BIDDING_API.get('/variables', { params: { ...reqBody, type: 'DAILY' } });
        const combinedData = { hourly: hourlyData.data, daily: dailyData.data };
        dispatch('mapVariables', { combinedData });
      } catch (error) {
        console.error(`Error Fetching ${params.type} Variables`, error);
      }
    }
  },
  updateHourlyValuesData({ state, commit }, { id, description, variableTypeChanged }) {
    const hourlyValues = [{ id, description }];
    const iteratingOverMap = state.dailyGlobalVariablesData;
    if (iteratingOverMap.length < 1) return;
    let hourlyData = {};
    iteratingOverMap.forEach((object) => {
      if (object.id === id) {
        hourlyData = object;
      }
    });

    if (variableTypeChanged) {
      const tz = dateStore.getTimeZone();
      const totalDayHours = dateStore.getTotalDatehours(state.startDate, tz);
      [...Array(totalDayHours).keys()].forEach((x) => { hourlyValues[0][`he${ x + 1 }`] = hourlyData.value; });
      hourlyData.config = {
        id,
        name: hourlyData.name,
        valueType: hourlyData.valueType,
        marketType: hourlyData.marketType,
        location: hourlyData.location,
      };
    } else {
      hourlyData.values.forEach((data) => { hourlyValues[0][`he${ data.he }`] = data.value; });
    }

    commit('setHourlyType', 'GLOBAL');
    commit('setHourlyValuesData', hourlyValues);
    commit('setHourlyValuesConfig', hourlyData.config);
  },
  async fetchAuditData({ commit }, { selectedRecord }) {
    const { id, variableType } = selectedRecord;
    let mappedData = [];
    try {
      const params = {
        tz: dateStore.getTimeZone(),
      };
      const { data } = await BIDDING_API.get(`/variables/${id}/revisions`, { params });
      if (data) {
        if (variableType === 'HOURLY') {
          data.forEach((variableData) => {
            variableData.hourlyValues.forEach((hourlyValue) => {
              // he01, 02 10
              variableData[`he${hourlyValue.hourEnding}`] = hourlyValue.value;
            });
          });
        }
        mappedData = data;

        mappedData.forEach((d) => {
          d.createdDate = moment(d.createdDate).format('YYYY-MM-DD hh:mm:ss A');
          d.effectiveDate = moment(d.effectiveDate).format('YYYY-MM-DD');
        });
      }
    } catch (ex) {
      mappedData = [];
      this.$notify('Failed to load audit logs for Variables', 'error');
    } finally {
      commit('setAuditData', mappedData);
    }
  },
  async mapVariables({ state, commit }, { combinedData, type, strategy }) {
    let tableData = [];
    let map = [];
    let variables = combinedData?.daily?.variables || [];

    // on DST days or past days, we don't want to allow carry forward, so set to false here so updates are forced to be false.
    const tz = dateStore.getTimeZone();
    const totalDayHours = dateStore.getTotalDatehours(state.startDate, tz);
    
    if (variables) {
      tableData = variables.map(({
        name, valueType, visualOrder, value, id, marketType, location, scriptName, description, carryForward, startTime
      }) => ({
        name,
        valueType,
        value,
        id,
        marketType,
        location,
        description,
        prevCarryForward: carryForward,
        carryForward: (totalDayHours != 24 || moment().startOf('day').isAfter(startTime)) ? false : carryForward,
        visualOrder,
        variableType: 'DAILY',
        strategies: scriptName?.split(',') || [],
      }));
    }
    variables = combinedData?.hourly?.variables || [];
    if (variables) {
      map = variables.reduce((acc, curr) => {
        let currHe = moment(curr.startTime).get('hour') + 1;
        if (curr.id in acc) {
          if (acc[curr.id].values.filter(({ he }) => he === currHe).length) {
            currHe = 25; // catches double HE2 for DST long day
          }
          acc[curr.id].values.push({
            he: currHe,
            value: curr.value,
          });
        } else {
          acc[curr.id] = {
            id: curr.id,
            name: curr.name,
            valueType: curr.valueType,
            marketType: curr.marketType,
            location: curr.location,
            variableType: 'HOURLY',
            visualOrder: curr.visualOrder,
            prevCarryForward: curr.carryForward,
            carryForward: (totalDayHours != 24 || moment().startOf('day').isAfter(curr.startTime)) ? false : curr.carryForward,
            description: curr.description,
            valueOptions: curr.valueOptions,
            value: isEmpty(curr.valueOptions) ? EMPTY_HOURLY_VALUE : curr.valueOptions,
            strategies: curr.scriptName?.split(',') || undefined,
            config: {
              id: curr.id,
              name: curr.name,
              valueType: curr.valueType,
              marketType: curr.marketType,
              location: curr.location,
              strategies: curr.scriptName?.split(',') || undefined,
            },
            values: [{
              he: currHe,
              value: curr.value,
            }],
          };
        }
        return acc;
      }, {});

      Object.keys(map).forEach((item) => (tableData.push(map[item])));
    }

    commit('setDailyGlobalVariablesData', tableData);
  },
  async fetchScriptCategories({ commit }) {
    try {
      const { data } = await SCRIPTING_API.get('categories');
      commit('setScriptCategories', data.data);
    } catch (error) {
      console.error('Error fetching script categories', error);
    }
  },
};

const mutations = {
  ...createMutations(
    'startDate',
    'variableDialogVisibility',
    'hourlyValuesData',
    'hourlyType',
    'hourlyValuesConfig',
    'auditData',
    'dailyGlobalVariablesData',
    'scriptCategories',
    'scripts',
    'moduleName',
    'entityType',
    'market',
    'commodity',
  ),
};

export default {
  namespaced: true,
  modules: { REFERENCE_DATA_STORE },
  state,
  getters,
  actions,
  mutations,
};
